// ChainDetectionModal.jsx
import React, { useState } from 'react';

const ChainDetectionModal = ({ chainDetection, searchResults, onCleanup, onClose }) => {
  const [selectedChains, setSelectedChains] = useState(new Set());
  const [customExclusions, setCustomExclusions] = useState('');
  const [keepRemoving, setKeepRemoving] = useState(true);

  const toggleChainSelection = (chainName) => {
    const newSelected = new Set(selectedChains);
    if (newSelected.has(chainName)) {
      newSelected.delete(chainName);
    } else {
      newSelected.add(chainName);
    }
    setSelectedChains(newSelected);
  };

  const handleCleanup = () => {
    if (selectedChains.size === 0 && !customExclusions.trim()) {
      return;
    }

    const selectedPlaceIds = [];
    const exclusionPatterns = [];

    chainDetection.chains.forEach(chain => {
      if (selectedChains.has(chain.normalized_name)) {
        chain.places.forEach(place => {
          selectedPlaceIds.push(place.id);
        });
        
        if (keepRemoving) {
          exclusionPatterns.push(chain.normalized_name);
        }
      }
    });

    if (customExclusions.trim()) {
      const customPatterns = customExclusions.split('\n').map(p => p.trim()).filter(p => p);
      exclusionPatterns.push(...customPatterns);
    }

    onCleanup(selectedPlaceIds, exclusionPatterns);
    onClose();
  };

  if (!chainDetection || !chainDetection.chains) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3.5 border-b border-gray-300 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">Chain Detection</h2>
              <p className="text-xs text-gray-600 mt-1">
                {chainDetection.unique_chain_count} chains • {chainDetection.total_chain_places} locations
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-light"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-160px)]">
          {/* Auto-excluded notice */}
          {chainDetection.auto_excluded_count > 0 && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
              <div className="flex items-start gap-2.5">
                <div className="text-red-500 text-lg">🚫</div>
                <div>
                  <h3 className="text-xs font-semibold text-red-800 uppercase tracking-wide">
                    Auto-Excluded
                  </h3>
                  <p className="text-xs text-red-700 mt-1.5 leading-relaxed">
                    {chainDetection.auto_excluded_count} places automatically excluded based on your patterns
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Chain List */}
          <div className="mb-4">
            <h3 className="text-xs font-semibold text-gray-700 mb-2.5 uppercase tracking-wide">Detected Chains</h3>
            <div className="space-y-2">
              {chainDetection.chains.map((chain, index) => (
                <div key={index} className="border border-gray-200 rounded p-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <input
                        type="checkbox"
                        checked={selectedChains.has(chain.normalized_name)}
                        onChange={() => toggleChainSelection(chain.normalized_name)}
                        className="h-4 w-4 text-blue-500 focus:ring-blue-400 border-gray-300 rounded"
                      />
                      <div>
                        <h4 className="font-medium text-gray-900 text-xs">{chain.chain_name}</h4>
                        <p className="text-xs text-gray-600 mt-0.5">
                          {chain.place_count} locations
                        </p>
                      </div>
                    </div>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      {chain.place_count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Exclusions */}
          <div className="mb-4">
            <h3 className="text-xs font-semibold text-gray-700 mb-2.5 uppercase tracking-wide">Additional Exclusions</h3>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Custom patterns (one per line)
              </label>
              <textarea
                value={customExclusions}
                onChange={(e) => setCustomExclusions(e.target.value)}
                placeholder="Enter business names to exclude..."
                className="w-full px-3 py-2 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                rows={4}
              />
              <p className="text-xs text-gray-500 mt-1.5">
                Auto-applied to future searches
              </p>
            </div>
          </div>

          {/* Keep Removing Checkbox */}
          {selectedChains.size > 0 && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <div className="flex items-start gap-2.5">
                <input
                  id="keep-removing"
                  type="checkbox"
                  checked={keepRemoving}
                  onChange={(e) => setKeepRemoving(e.target.checked)}
                  className="h-4 w-4 text-blue-500 focus:ring-blue-400 border-gray-300 rounded mt-0.5"
                />
                <div className="text-xs">
                  <label htmlFor="keep-removing" className="font-medium text-gray-900 cursor-pointer">
                    Keep removing for future searches
                  </label>
                  <p className="text-gray-600 mt-1 leading-relaxed">
                    Auto-exclude selected chains from future results
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3.5 border-t border-gray-300 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-600">
              {selectedChains.size > 0 && (
                <span>
                  {selectedChains.size} chain{selectedChains.size !== 1 ? 's' : ''} selected
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-3 py-2 text-gray-700 border border-gray-300 rounded text-xs font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCleanup}
                disabled={selectedChains.size === 0 && !customExclusions.trim()}
                className="px-3 py-2 bg-red-500 text-white rounded text-xs font-medium hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Remove & Exclude
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChainDetectionModal;